import type { SelectOption } from '../../ui/index.ts';
import { Button, Chip, Input, Select, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { useT } from '../../i18n/index.ts';
import { validationIssueKey } from './labels.ts';
import {
  decodeUpstreamSelector,
  decodeVia,
  encodeUpstreamSelector,
  encodeVia,
  type EgressPolicyDraftRule,
  type EgressPolicyRuleRow,
} from './policy-rules.ts';

export function EgressPolicyRuleTable({
  rows,
  ingressOptions,
  upstreamOptions,
  viaOptions,
  onChange,
  onRemove,
}: {
  readonly rows: readonly EgressPolicyRuleRow[];
  readonly ingressOptions: readonly SelectOption[];
  readonly upstreamOptions: readonly SelectOption[];
  readonly viaOptions: readonly SelectOption[];
  readonly onChange: (key: string, next: EgressPolicyDraftRule) => void;
  readonly onRemove: (key: string) => void;
}): JSX.Element {
  const t = useT();
  const fallbackOptions: SelectOption[] = [
    { value: 'direct', label: t('egress_lines.policies.fallback.direct') },
    { value: 'none', label: t('egress_lines.policies.fallback.none') },
  ];

  return (
    <Table>
      <THead>
        <Tr>
          <Th>{t('egress_lines.policies.col.index')}</Th>
          <Th>{t('egress_lines.policies.col.ingress')}</Th>
          <Th>{t('egress_lines.policies.col.upstream')}</Th>
          <Th>{t('egress_lines.policies.col.via')}</Th>
          <Th>{t('egress_lines.policies.col.fallback')}</Th>
          <Th>{t('egress_lines.policies.col.source')}</Th>
          <Th>{t('egress_lines.policies.col.note')}</Th>
          <Th />
        </Tr>
      </THead>
      <TBody>
        {rows.map((row, index) => (
          <Tr
            key={row.key}
            className={row.inherited ? 'bg-well text-paper-low' : undefined}
            data-testid={row.inherited ? `egress-policy-inherited-${index}` : `egress-policy-own-${index}`}
          >
            <Td className="font-mono text-micro">{index + 1}</Td>
            {row.inherited ? (
              <InheritedCells row={row} />
            ) : (
              <OwnCells
                row={row}
                ingressOptions={ingressOptions}
                upstreamOptions={upstreamOptions}
                viaOptions={viaOptions}
                fallbackOptions={fallbackOptions}
                onChange={onChange}
                onRemove={onRemove}
              />
            )}
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function inheritedUpstreamLabel(
  draft: EgressPolicyDraftRule,
  t: ReturnType<typeof useT>,
): string {
  if (
    (draft.upstream_id === null || draft.upstream_id.length === 0)
    && draft.upstream_region !== null
    && draft.upstream_region.length > 0
  ) {
    return t('egress_lines.policies.upstream.region', { region: draft.upstream_region });
  }
  return encodeUpstreamSelector(draft);
}

function upstreamOptionsForDraft(
  options: readonly SelectOption[],
  draft: EgressPolicyDraftRule,
  t: ReturnType<typeof useT>,
): readonly SelectOption[] {
  const encoded = encodeUpstreamSelector(draft);
  if (!encoded.startsWith('region:')) return options;
  if (options.some((option) => option.value === encoded)) {
    return options.map((option) => (
      option.value === encoded ? { ...option, disabled: true } : option
    ));
  }
  const region = draft.upstream_region ?? encoded.slice('region:'.length);
  return [
    ...options,
    {
      value: encoded,
      label: t('egress_lines.policies.upstream.region_legacy', { region }),
      disabled: true,
    },
  ];
}

function InheritedCells({ row }: { readonly row: EgressPolicyRuleRow }): JSX.Element {
  const t = useT();
  return (
    <>
      <Td className="font-mono text-dense">{row.draft.ingress_region}</Td>
      <Td className="font-mono text-dense">{inheritedUpstreamLabel(row.draft, t)}</Td>
      <Td className="font-mono text-dense">{encodeVia(row.draft)}</Td>
      <Td className="text-dense">
        {row.draft.via_kind === 'direct' ? t('egress_lines.policies.fallback.na') : row.draft.fallback}
      </Td>
      <Td>
        <Chip tone="neutral" lamp={false}>{t('egress_lines.policies.source.inherited')}</Chip>
      </Td>
      <Td className="text-dense text-paper-low">
        {row.draft.note.length === 0 ? t('egress_lines.policies.fallback.na') : row.draft.note}
      </Td>
      <Td />
    </>
  );
}

function OwnCells({
  row,
  ingressOptions,
  upstreamOptions,
  viaOptions,
  fallbackOptions,
  onChange,
  onRemove,
}: {
  readonly row: EgressPolicyRuleRow;
  readonly ingressOptions: readonly SelectOption[];
  readonly upstreamOptions: readonly SelectOption[];
  readonly viaOptions: readonly SelectOption[];
  readonly fallbackOptions: readonly SelectOption[];
  readonly onChange: (key: string, next: EgressPolicyDraftRule) => void;
  readonly onRemove: (key: string) => void;
}): JSX.Element {
  const t = useT();
  const draft = row.draft;
  const viaValue = encodeVia(draft);
  const fallbackDisabled = draft.via_kind === 'direct';

  return (
    <>
      <Td>
        <Select
          value={draft.ingress_region}
          options={ingressOptions}
          onValueChange={(value) => onChange(draft.key, { ...draft, ingress_region: value })}
          testId={`egress-policy-ingress-${draft.key}`}
        />
      </Td>
      <Td>
        <Select
          value={encodeUpstreamSelector(draft)}
          options={upstreamOptionsForDraft(upstreamOptions, draft, t)}
          onValueChange={(value) => onChange(draft.key, { ...draft, ...decodeUpstreamSelector(value) })}
          testId={`egress-policy-upstream-${draft.key}`}
        />
      </Td>
      <Td>
        <Select
          value={viaValue}
          options={viaOptions}
          onValueChange={(value) => {
            const via = decodeVia(value);
            onChange(draft.key, {
              ...draft,
              ...via,
              fallback: via.via_kind === 'direct' ? 'direct' : draft.fallback,
            });
          }}
          testId={`egress-policy-via-${draft.key}`}
        />
      </Td>
      <Td>
        <Select
          value={draft.fallback}
          options={fallbackOptions}
          disabled={fallbackDisabled}
          onValueChange={(value) =>
            onChange(draft.key, { ...draft, fallback: value === 'none' ? 'none' : 'direct' })
          }
          testId={`egress-policy-fallback-${draft.key}`}
        />
      </Td>
      <Td>
        <Chip tone="signal" lamp={false}>{t('egress_lines.policies.source.own')}</Chip>
      </Td>
      <Td>
        <Input
          value={draft.note}
          onChange={(event) => onChange(draft.key, { ...draft, note: event.target.value })}
          data-testid={`egress-policy-note-${draft.key}`}
        />
        {row.issue !== null ? (
          <IssueText issue={row.issue} testId={`egress-policy-issue-${draft.key}`} />
        ) : null}
      </Td>
      <Td>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(draft.key)}
          data-testid={`egress-policy-remove-${draft.key}`}
        >
          {t('egress_lines.policies.delete_rule')}
        </Button>
      </Td>
    </>
  );
}

function IssueText({ issue, testId }: { readonly issue: string; readonly testId: string }): JSX.Element {
  const t = useT();
  const key = validationIssueKey(issue);
  return (
    <p className="m-0 mt-1 text-micro text-bad" role="alert" data-testid={testId}>
      {key === null ? issue : t(key)}
    </p>
  );
}
